import { get, put, del, post, ApiError } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import APIError from 'app/shared-components/error/APIError';
import SecurityError from 'app/shared-components/error/SecurityError';
import jwtService from '../../auth/services/jwtService';
export const clientVersion= 144; //Manually update before deployment with any changes that need to force refresh!

//bump trigger: 10

function buildPathWithQuery(path, queryStringParameters) {
	if (!queryStringParameters) return path;
	const params = new URLSearchParams(queryStringParameters);
	return path + '?' + params.toString();
}

function removeQueryParams(options) {
	if (!options?.queryStringParameters) return options;
	const { queryStringParameters, ...rest } = options;
	return rest;
}

export async function apiGet(apiName, path, options){
   if (process.env.REACT_APP_LOCAL_AUTH === 'true') {
      return await fetchLocal('GET', path, options);
   }
   const finalPath = buildPathWithQuery(path, options?.queryStringParameters);
   const finalOptions = removeQueryParams(options);
   const restOperation = get({
	    apiName,
	    path: finalPath,
	    options: await addAuthorization(apiName, finalOptions),
	  });
   return await execute(restOperation);
}

function removeNullValues(obj) {
   // Only process plain objects
   if (!obj || typeof obj !== 'object' || Array.isArray(obj) || obj instanceof Date) {
      return obj;
   }
   
   // Check if it's a plain object (not a class instance)
   if (obj.constructor !== Object) {
      return obj;
   }
   
   // Only remove top-level null values
   const cleaned = {};
   for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== null) {
         cleaned[key] = obj[key];
      }
   }
   return cleaned;
}


export async function apiPut(apiName, path, options){
   if (process.env.REACT_APP_LOCAL_AUTH === 'true') {
      return await fetchLocal('PUT', path, options);
   }
   const finalPath = buildPathWithQuery(path, options?.queryStringParameters);
   const cleanedOptions = options?.body ? { ...options, body: removeNullValues(options.body) } : options;
   const finalOptions = removeQueryParams(cleanedOptions);
   const restOperation = put({
	    apiName,
	    path: finalPath,
	    options: await addAuthorization(apiName, finalOptions),
	  });
   return await execute(restOperation);
}

export async function apiDel(apiName, path, options){
   if (process.env.REACT_APP_LOCAL_AUTH === 'true') {
      return await fetchLocal('DELETE', path, options);
   }
   const finalPath = buildPathWithQuery(path, options?.queryStringParameters);
   const finalOptions = removeQueryParams(options);
   const restOperation = del({
	    apiName,
	    path: finalPath,
	    options: await addAuthorization(apiName, finalOptions),
	  });
   return await execute(restOperation);
}

export async function apiPost(apiName, path, options){
   if (process.env.REACT_APP_LOCAL_AUTH === 'true') {
      return await fetchLocal('POST', path, options);
   }
   const finalPath = buildPathWithQuery(path, options?.queryStringParameters);
   const cleanedOptions = options?.body ? { ...options, body: removeNullValues(options.body) } : options;
   const finalOptions = removeQueryParams(cleanedOptions);
   const restOperation = post({
	    apiName,
	    path: finalPath,
	    options: await addAuthorization(apiName, finalOptions),
	  });
	return await execute(restOperation);
}


async function execute(restOperation){
	try {
	  const { body, headers } = await restOperation.response;
	  const version= headers['x-client-version'];
     if (version > clientVersion) { // if server version newer
        console.log("Newer client version exists, forcing refresh at next route change.");
        if (window.localStorage) window.localStorage.setItem('version-update-needed', 'true'); // set version update item so can refresh app later
     };
	  return await body.json();
	} catch (error) {
	  if (error instanceof ApiError) {
	    if (error.response) {
	      const { 
	        statusCode, 
	        headers, 
	        body 
	      } = error.response;
	      console.error(`Received ${statusCode} error response with payload: ${body}`);
	      const data = body ? JSON.parse(body) : {};
	      const message= data.message || data.error || "Unknown Error";

	      if (statusCode == 401) {
			console.error("Detected Authorization Error.");
			throw new SecurityError(message);
		  }
	      else throw new APIError(message, statusCode, data);
	    }
	  } else throw error;
	}
}

async function fetchLocal(method, path, options) {
	const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
	const authToken = jwtService.getAccessToken();
	
	const headers = {
		'Content-Type': 'application/json',
		...(options?.headers || {})
	};
	
	if (authToken) {
		headers['Authorization'] = authToken;
	}
	
	const fetchOptions = {
		method,
		headers
	};
	
	if (options?.body) {
		const cleanedBody = removeNullValues(options.body);
		fetchOptions.body = JSON.stringify(cleanedBody);
	}
	
	let url = apiUrl + path;
	if (options?.queryStringParameters) {
		const params = new URLSearchParams(options.queryStringParameters);
		url += '?' + params.toString();
	}
	
	const response = await fetch(url, fetchOptions);
	
	if (!response.ok) {
		const errorText = await response.text();
		const data = errorText ? JSON.parse(errorText) : {};
		const message = data.message || data.error || "Unknown Error";
		
		if (response.status === 401) {
			console.error("Detected Authorization Error.");
			throw new SecurityError(message);
		}
		throw new APIError(message, response.status, data);
	}
	
	return await response.json();
}

async function addAuthorization(apiName, options){
	if (apiName.includes('Public')) return options;
	const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
	return {... options, headers: { Authorization: authToken }};
}

